import { type PropsWithChildren } from 'react'
import { Elements } from '@stripe/react-stripe-js'
import { type Stripe, type StripeElementsOptionsMode } from '@stripe/stripe-js'
import { useTheme } from 'styled-components'
import { getStripeAmountToCharge } from '../../utils/utils'
import {
  type CheckoutContextValue,
  useCheckout,
} from '../Checkout/CheckoutContext'
import { useLanguage } from '../../services/LangProvider'
import {
  type LANGUAGES,
  SUPPORTED_LANGUAGES,
} from '../../locale/Translation.consts'

const isSupportedLanguage = (language: string): language is LANGUAGES =>
  SUPPORTED_LANGUAGES.some(
    (supportedLanguage) => supportedLanguage === language
  )

export function getStripePaymentOptions(
  pricingSummary: CheckoutContextValue['pricingSummary']
) {
  if (!(pricingSummary?.totalPrice > 0 && pricingSummary?.currency)) {
    return null
  }
  const amount = getStripeAmountToCharge({
    totalCost: pricingSummary.totalPrice,
    currency: pricingSummary.currency,
  })
  if (amount <= 0) {
    return null
  }
  return {
    mode: 'payment' as const,
    amount,
    currency: pricingSummary.currency.toLowerCase(),
  }
}

function StripeElementsProvider({
  children,
  stripe,
}: PropsWithChildren<{ stripe: Stripe | null }>) {
  const { isCheckoutOpen, pricingSummary, isLoading } = useCheckout()
  const theme = useTheme()
  const language = useLanguage()
  const paymentOptions =
    (isCheckoutOpen && !isLoading && getStripePaymentOptions(pricingSummary)) ||
    {}

  const options: StripeElementsOptionsMode = {
    locale: isSupportedLanguage(language) ? language : 'auto',
    ...paymentOptions,
    externalPaymentMethodTypes: ['external_paypal'],
    appearance: {
      variables: {
        colorText: theme.colors.black,
        colorTextPlaceholder: theme.colors.grayDark,
        colorTextSecondary: theme.colors.secondaryDark,
        colorPrimary: theme.colors.secondaryDark, // In order to change Link text color (in Card section)
        spacingGridRow: '20px',
        colorDanger: theme.colors.errorRed,
        fontFamily: 'Mixtiles Sans, sans-serif',
      },
      rules: {
        '.Input': {
          borderWidth: '1.5px',
        },
        '.Input:focus': {
          outline: 'none',
          boxShadow: 'none',
          borderColor: theme.colors.black,
        },
        '.AccordionItem': {
          color: theme.colors.secondaryDark,
        },
        '.AccordionItem--selected': {
          color: theme.colors.primary,
        },
      },
    },
    fonts: [],
  }

  return (
    <Elements stripe={stripe} options={options}>
      {children}
    </Elements>
  )
}

export default StripeElementsProvider
